/**
 * CancelShipmentDrawer — KAN-13 "Cancelar envio"
 *
 * Cancela 1 sale_item individual com motivo obrigatório.
 * Backend: POST /api/v2/sales/items/:saleItemId/cancel
 * Spec: docs/V2/07_REGRAS_NEGOCIO.md §5.2
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { salesV2API } from '../../services/api/v2/sales';
import { parseApiError, scrollToFirstError } from '../../utils/apiErrors';
import Drawer from '../products/details/Drawer';

const CancelShipmentDrawer = ({ isOpen, onClose, card, onCancelled }) => {
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setErrors({});
    setTopError('');
  }, [isOpen, card?.saleItemId]);

  const validate = () => {
    const err = {};
    const trimmed = reason.trim();
    if (!trimmed) err.reason = 'Informe o motivo do cancelamento';
    else if (trimmed.length < 3) err.reason = 'Motivo deve ter no mínimo 3 caracteres';
    else if (trimmed.length > 500) err.reason = 'Máximo 500 caracteres';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    if (!card?.saleItemId) {
      setTopError('Item da venda não identificado.');
      return;
    }
    try {
      setSubmitting(true);
      setTopError('');
      await salesV2API.cancelItem(card.saleItemId, { reason: reason.trim() });
      toast.success('Envio cancelado. Cliente notificado por email.');
      onCancelled?.(card);
      onClose?.();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao cancelar envio');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setTopError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const isShared = card?.kan17?.isShared;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Cancelar envio"
      subtitle={card?.kan17?.badge || (card?.saleId ? `Venda #${card.saleId}` : '')}
      icon="mdi:truck-remove-outline"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Confirmar cancelamento"
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-alert drawer-alert--warning">
        <Icon icon="mdi:alert-outline" width="18" />
        <div>
          <strong>Esta ação não pode ser desfeita.</strong> Ao confirmar:
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13 }}>
            <li>O envio é movido pra coluna CANCELADOS</li>
            <li>Código de rastreio (se houver) é limpo</li>
            <li>Estoque é creditado de volta</li>
            <li>Comissões dos participantes são revertidas proporcionalmente</li>
            <li>Cliente recebe email avisando</li>
            {isShared && (
              <li>
                Os <b>demais {card.kan17.total - 1} itens</b> da venda <b>#{card.saleId}</b> seguem normalmente
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Item</span>
        <div className="drawer-input" style={{ background: '#f9fafb', cursor: 'default' }}>
          {card?.description || `Item #${card?.saleItemId}`}
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Cliente</span>
        <div className="drawer-input" style={{ background: '#f9fafb', cursor: 'default' }}>
          {card?.name || '—'}
          {card?.email ? ` · ${card.email}` : ''}
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Motivo do cancelamento</span>
        <textarea
          className="drawer-input"
          rows={4}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setErrors((p) => ({ ...p, reason: undefined }));
          }}
          placeholder="Ex: Produto avariado antes do envio, cliente solicitou cancelamento, erro de endereço sem correção possível…"
          maxLength={500}
          data-field="reason"
        />
        <span className="drawer-help">
          {reason.length}/500 caracteres. Registrado no histórico junto com seu nome.
        </span>
        {errors.reason && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.reason}
          </span>
        )}
      </div>
    </Drawer>
  );
};

export default CancelShipmentDrawer;
